<template>
  <v-card flat shrink id="project_dashboard">
    <v-toolbar flat color="grey lighten-3">
      <v-toolbar-title>
        <v-icon class="mr-2" v-if="project.readOnly">mdi-pencil-off</v-icon>
        <span id="doctitle">{{ $t('app.label.dashboard') }}</span>
      </v-toolbar-title>
    </v-toolbar>
    <div id="project_dashboard_info">
      <v-row dense class="mt-2">
        <v-col md="2">
          <ImageSelector
              :initialImageUrl="projectImageUrl"
              ref="imageSelector"
              @image-change="image = $event"
          ></ImageSelector>
          <v-btn
              small
              :style="{visibility: image ? 'visible' : 'hidden'}"
              class="mt-1 ml-5"
              color="primary"
              :loading="saveProjectImageLoading"
              @click="saveProjectImageWithLoading"
          >
            {{ $t('app.label.saveProjectImage') }}
          </v-btn>
          <v-checkbox
              v-if="canChangeProjectConfig && canSeeCampaign"
              class="ml-3"
              v-model="collaborativeAnnotationModel"
              :label="$t('createProject.fields.collaborativeAnnotation')"
          ></v-checkbox>
        </v-col>
        <v-col md="4">
          <div
              @mouseover="showEditProjectLabelIcon = true"
              @mouseleave="showEditProjectLabelIcon = false"
          >
            <v-text-field
                v-if="canChangeProjectConfig"
                v-model="projectLabel"
                :rules="[v => !!v || $i18n.t('app.message.projectLabelRequired')]"
                :label="$t('app.label.projectLabel')"
                :readonly="!editProjectLabel || readOnlyProject || nameMode"
                :class="editProjectLabel ? '' : 'removeBorder'"
                @keyup.esc.prevent="stopEditProjectLabel()"
                @keyup.enter.prevent="maybeSaveProjectLabel()"
            >
              <template #prepend>
                <v-tooltip bottom open-delay="100">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        small
                        @click="editProjectLabel = !editProjectLabel"
                        color="primary"
                    >
                      <v-icon>{{ showEditProjectLabelIcon ? 'mdi-pencil' : 'mdi-blank' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('app.button.edit') }}</span>
                </v-tooltip>
              </template>
              <template #append-outer v-if="editProjectLabel">
                <v-btn
                    small
                    @click="stopEditProjectLabel"
                    class="mr-2"
                    text
                >
                  {{ $t('app.button.cancel') }}
                </v-btn>
                <v-btn
                    small
                    @click="maybeSaveProjectLabel"
                    :disabled="!projectLabel"
                    color="primary"
                >
                  {{ $t('app.button.save') }}
                </v-btn>
              </template>
            </v-text-field>
            <v-text-field
                v-else
                v-model="projectLabel"
                :label="$t('app.label.projectLabel')"
                readonly
                class="removeBorder"
                prepend-icon="mdi-blank"
            ></v-text-field>
          </div>
          <div
              @mouseover="showEditProjectDescriptionIcon = true"
              @mouseleave="showEditProjectDescriptionIcon = false"
          >
            <v-text-field
                v-if="canChangeProjectConfig"
                v-model="projectDescription"
                :label="$t('app.label.projectDescription')"
                :readonly="!editProjectDescription || readOnlyProject"
                :class="editProjectDescription ? '' : 'removeBorder'"
                @keyup.esc.prevent="stopEditProjectDescription()"
                @keyup.enter.prevent="maybeSaveProjectDescription()"
            >
              <template #prepend>
                <v-tooltip bottom open-delay="100">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        small
                        @click="editProjectDescription = !editProjectDescription"
                        color="primary"
                    >
                      <v-icon>{{ showEditProjectDescriptionIcon ? 'mdi-pencil' : 'mdi-blank' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('app.button.edit') }}</span>
                </v-tooltip>
              </template>
              <template #append-outer v-if="editProjectDescription">
                <v-btn
                    small
                    @click="stopEditProjectDescription"
                    class="mr-2"
                    text
                >
                  {{ $t('app.button.cancel') }}
                </v-btn>
                <v-btn
                    small
                    @click="maybeSaveProjectDescription"
                    color="primary"
                >
                  {{ $t('app.button.save') }}
                </v-btn>
              </template>
            </v-text-field>
            <v-text-field
                v-else
                v-model="projectDescription"
                :label="$t('app.label.projectDescription')"
                readonly
                class="removeBorder"
                prepend-icon="mdi-blank"
            ></v-text-field>
          </div>
          <div
              @mouseover="showEditProjectDateIcon = true"
              @mouseleave="showEditProjectDateIcon = false"
          >
            <v-text-field
                v-if="canChangeProjectConfig && nameMode"
                v-model="projectDate"
                :rules="[v => !!v || $i18n.t('app.message.projectDateRequired')]"
                :label="$t('app.label.createdDate')"
                :readonly="!editProjectDate || readOnlyProject"
                :class="editProjectDate ? '' : 'removeBorder'"
                @keyup.esc.prevent="stopEditProjectDate()"
                @keyup.enter.prevent="maybeSaveProjectDate()"
            >
              <template #prepend>
                <v-tooltip bottom open-delay="100">
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        icon
                        small
                        @click="editProjectDate = !editProjectDate"
                        color="primary"
                    >
                      <v-icon>{{ showEditProjectDateIcon ? 'mdi-pencil' : 'mdi-blank' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('app.button.edit') }}</span>
                </v-tooltip>
              </template>
              <template #append-outer v-if="editProjectDate">
                <v-btn
                    small
                    @click="stopEditProjectDate"
                    class="mr-2"
                    text
                >
                  {{ $t('app.button.cancel') }}
                </v-btn>
                <v-btn
                    small
                    @click="maybeSaveProjectDate"
                    color="primary"
                >
                  {{ $t('app.button.save') }}
                </v-btn>
              </template>
            </v-text-field>
            <v-text-field
                v-else
                :value="moment(project.createdDate).fromNow()"
                :label="$t('app.label.createdDate')"
                prepend-icon="mdi-blank"
                readonly
                class="removeBorder"
            ></v-text-field>
          </div>
          <v-text-field
              :value="project.createdBy"
              :label="$t('app.label.createdBy')"
              readonly
              prepend-icon="mdi-blank"
              class="removeBorder"
          ></v-text-field>
        </v-col>
        <v-col md="2">
          <v-text-field
              :value="$t(`createProject.projectNature.${project.nature}`)"
              :label="$t('app.label.nature')"
              readonly
              prepend-icon="mdi-blank"
              class="removeBorder"
          ></v-text-field>
          <v-text-field
              :value="$t(`languages.${project.lang}`)"
              :label="$t('app.label.language')"
              prepend-icon="mdi-blank"
              readonly
              class="removeBorder"
          ></v-text-field>
          <v-text-field
              v-if="owner"
              :value="owner"
              :label="$t('app.label.owner')"
              readonly
              prepend-icon="mdi-blank"
              class="removeBorder"
          ></v-text-field>
          <v-text-field
              v-if="groupName"
              :value="groupName"
              :label="$t('app.label.group')"
              readonly
              prepend-icon="mdi-blank"
              class="removeBorder"
          ></v-text-field>
        </v-col>
        <v-col md="4">
<!--          <v-text-field-->
<!--              :value="$t(`app.label.${privateProject ? 'private' : 'shared'}`)"-->
<!--              :label="$t('app.label.visibility')"-->
<!--              readonly-->
<!--              class="removeBorder"-->
<!--              :prepend-icon="privateProject ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline'"-->
<!--          ></v-text-field>-->
          <GroupsShares
              @add-mode="addMode"
              @share-deleted="groupShareDeleted"
          ></GroupsShares>
          <UsersShares
              @add-mode="addMode"
              @share-deleted="userShareDeleted"
          ></UsersShares>
        </v-col>
      </v-row>
    </div>
    <div v-if="nbOpenShares === 0" id="project_dashboard_status">
      <v-container>
        <v-row>
          <v-col md="6">
            <v-card>
              <v-toolbar :class="corpusClass">
                <v-toolbar-title>{{ $t("dataset.corpus.corpus") }}</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col>{{ $tc("dataset.common.documents", stats.corpus.documentCount) }}</v-col>
                    <v-col id="stats_corpus-docs">{{ stats.corpus.documentCount }}</v-col>
                  </v-row>
                  <v-row v-if="stats.corpus.segmentCount">
                    <v-col>{{ $tc("dataset.common.segments", stats.corpus.segmentCount) }}</v-col>
                    <v-col id="stats_corpus-segs">{{ stats.corpus.segmentCount }}</v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col md="6" v-if="!isQuestionAnsweringProject">
            <v-card>
              <v-toolbar :class="datasetClass">
                <v-toolbar-title>{{ $t("app.label.dataset") }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-tooltip
                    v-if="datasetStatus !== 'green'"
                    top
                    open-delay="600"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        :id="`btn-explore`"
                        text
                        icon
                        small
                        to="/explore"
                    >
                      <v-icon>mdi-compass</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("app.label.explore") }}</span>
                </v-tooltip>
              </v-toolbar>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col>{{ $tc("dataset.common.documents", stats.annotation.documentsInDataset) }}</v-col>
                    <v-col
                        v-if="isSequenceLabellingProject && stats.annotation"
                        id="stats_dataset-adocs"
                    >{{ stats.annotation.documentsInDataset }}
                    </v-col>
                    <v-col
                        v-if="isClassificationProject && stats.categories"
                        id="stats_dataset-cdocs"
                    >{{ stats.categories.documentsInDataset }}
                    </v-col>
                  </v-row>
                  <v-row v-if="stats.annotation.segmentsInDataset">
                    <v-col>{{ $tc("dataset.common.segments", stats.annotation.segmentsInDataset) }}</v-col>
                    <v-col
                        v-if="isSequenceLabellingProject && stats.annotation"
                        id="stats_dataset-asegs"
                    >{{ stats.annotation.segmentsInDataset }}
                    </v-col>
                    <v-col
                        v-if="isClassificationProject && stats.categories"
                        id="stats_dataset-csegs"
                    >{{ stats.categories.segmentsInDataset }}
                    </v-col>
                  </v-row>
                  <v-row v-if="isSequenceLabellingProject && stats.annotation.annotationCount">
                    <v-col>{{ $tc('dataset.common.annotations', stats.annotation.annotationCount) }}</v-col>
                    <v-col id="stats_dataset-annots">{{ stats.annotation.annotationCount }}</v-col>
                  </v-row>
                  <v-row v-if="isClassificationProject && stats.categories.categoriesCount">
                    <v-col>{{ $tc('dataset.common.categories', stats.categories.categoriesCount) }}</v-col>
                    <v-col id="stats_dataset-categos">{{ stats.categories.categoriesCount }}</v-col>
                  </v-row>
                </v-container>
              </v-card-text>
            </v-card>
          </v-col>
          <!--          <v-col v-if="!isQuestionAnsweringProject">-->
          <!--            <v-card>-->
          <!--              <v-toolbar :class="modelClass">-->
          <!--                <v-toolbar-title>{{ $t("dataset.model.model") }}</v-toolbar-title>-->
          <!--                <v-spacer></v-spacer>-->
          <!--                <v-tooltip v-if="modelStatus !== 'green'" top open-delay="600">-->
          <!--                  <template v-slot:activator="{ on }">-->
          <!--                    <v-btn-->
          <!--                        v-on="on"-->
          <!--                        :id="`btn-suggestions`"-->
          <!--                        text-->
          <!--                        icon-->
          <!--                        small-->
          <!--                        to="/suggestions"-->
          <!--                    >-->
          <!--                      <v-icon>mdi-lightbulb-on-outline</v-icon>-->
          <!--                    </v-btn>-->
          <!--                  </template>-->
          <!--                  <span>{{ $t("app.label.suggestions") }}</span>-->
          <!--                </v-tooltip>-->
          <!--              </v-toolbar>-->
          <!--              <v-card-text>-->
          <!--                <v-container>-->
          <!--                  <v-row v-if="stats.modelInfos.best">-->
          <!--                    <v-col>{{ $t("expSugCommons.label.f1") }}</v-col>-->
          <!--                    <v-col id="stats_model-fmeasure">-->
          <!--                      {{-->
          <!--                        stats.modelInfos.best.report ? stats.modelInfos.best.report.microAvg.f1 : 0 | numeral("0.[0]%")-->
          <!--                      }}-->
          <!--                    </v-col>-->
          <!--                  </v-row>-->
          <!--                  <v-row v-if="stats.modelInfos.best">-->
          <!--                    <v-col>support</v-col>-->
          <!--                    <v-col id="stats_model-support">-->
          <!--                      {{ stats.modelInfos.best.report ? stats.modelInfos.best.report.microAvg.support : 0 }}-->
          <!--                    </v-col>-->
          <!--                  </v-row>-->
          <!--                  <v-row v-else>-->
          <!--                    <v-col>{{ $t("dataset.model.noModel") }}</v-col>-->
          <!--                  </v-row>-->
          <!--                </v-container>-->
          <!--              </v-card-text>-->
          <!--            </v-card>-->
          <!--          </v-col>-->
        </v-row>
      </v-container>
    </div>
  </v-card>
</template>

<script>
import {mapState, mapActions, mapGetters, mapMutations} from "vuex";
import moment from "moment";
import GroupsShares from "./GroupsShares"
import UsersShares from "./UsersShares"
import {api, rootURL} from "@/api"
import _ from "lodash"
import ImageSelector from "@/components/createProject/ImageSelector";

export default {
  components: {
    ImageSelector,
    GroupsShares,
    UsersShares,
  },
  data() {
    return {
      image: null,
      saveProjectImageLoading: false,
      ownsProject: false,
      nbOpenShares: 0,
      editProjectLabel: false,
      editProjectDescription: false,
      editProjectDate: false,
      newProjectLabel: "",
      newProjectDate: "",
      newProjectDescription: "",
      showEditProjectLabelIcon: false,
      showEditProjectDescriptionIcon: false,
      showEditProjectDateIcon: false,
      privateProject: true,
      owner: "",
      groupName: ""
    };
  },
  //props: ["statistics"],
  mounted() {
    this.stopEditProjectLabel(this.project.name)

    this.initProjectShares(this.project.name)
    this.getAdditionalProjectInfo()
  },
  computed: {
    ...mapState(["project", "nameMode"]),
    ...mapState("auth", ["user"]),
    ...mapState("projectShares", ["groupsShares", "usersShares"]),
    ...mapGetters(["readOnlyProject", "isSequenceLabellingProject", "collaborativeAnnotation", "isClassificationProject", "isQuestionAnsweringProject"]),
    ...mapGetters("auth", [
      "canExportProject",
      "canImportDocuments",
      "canChangeProjectConfig",
      "canSeeCampaign"
    ]),
    ...mapState(["project"]),
    ...mapState("dataset", ["stats"]),
    projectImageUrl() {
      return `${rootURL}${this.project.image}`
    },
    collaborativeAnnotationModel: {
      get() {
        return this.collaborativeAnnotation;
      },
      set() {
        this.toggleCollaborativeAnnotation();
      }
    },
    projectLabel: {
      get() {
        return this.nameMode ? this.project.name : (this.editProjectLabel ? this.newProjectLabel : (this.project.label ? this.project.label : null))
      },
      set(value) {
        this.newProjectLabel = value;
      },
    },
    projectDescription: {
      get() {
        return (this.editProjectDescription ? this.newProjectDescription : (this.project.description ? this.project.description : null))
      },
      set(value) {
        this.newProjectDescription = value;
      },
    },
    projectDate: {
      get() {
        return this.project.createdDate
      },
      set(value) {
        this.newProjectDate = value;
      },
    },
    corpusStatus: function () {
      if (!_.get(this, 'stats.corpus'))
        return ""
      if (_.get(this, 'stats.corpus.documentCount', 0) > 0) {
        if (_.get(this, 'stats.corpus.segmentCount', 0) > 0) {
          return "green";
        } else {
          return "orange";
        }
      } else {
        return "red";
      }
    },
    datasetStatus: function () {
      if (!_.get(this, 'stats.annotation') && !_.get(this, 'stats.categories'))
        return "red"
      const cdocumentsInDataset = _.get(this, 'stats.categories.documentsInDataset', 0)
      const adocumentsInDataset = _.get(this, 'stats.annotation.documentsInDataset', 0)
      if (
          cdocumentsInDataset > 0 || adocumentsInDataset > 0
      ) {
        if (
            cdocumentsInDataset > 20 ||
            adocumentsInDataset > 20
        ) {
          return "green";
        } else {
          return "orange";
        }
      } else {
        return "red";
      }
    },
    modelStatus: function () {
      if (!_.get(this, 'stats.modelInfos'))
        return ""
      if (
          _.get(this, 'stats.modelInfos.history', []).length > 0
      ) {
        if (
            _.get(this, 'stats.modelInfos.best.report.microAvg.f1', 0) > 0.33 &&
            _.get(this, 'stats.modelInfos.best.report.microAvg.support', 0) > 20
        ) {
          return "green";
        } else {
          return "orange";
        }
      } else {
        return "red";
      }
    },
    corpusClass: function () {
      return this.corpusStatus + " lighten-3";
    },
    datasetClass: function () {
      return this.datasetStatus + " lighten-3";
    },
    modelClass: function () {
      return this.modelStatus + " lighten-3";
    },
  },
  methods: {
    ...mapActions("projectShares", ["initProjectShares"]),
    ...mapMutations(["setAddDocumentPopup"]),
    ...mapActions("home", ["exportProject"]),
    ...mapActions(["saveProjectLabel", "saveProjectDescription", "saveProjectDate", "saveProjectImage", "toggleCollaborativeAnnotation"]),
    saveProjectImageWithLoading() {
      try {
        this.saveProjectImageLoading = true
        this.saveProjectImage(this.image)
      } finally {
        this.saveProjectImageLoading = false
        this.image = null
      }
    },
    userShareDeleted(username) {
      if (this.user.username === username)
        this.$router.push({name: 'home'})
    },
    groupShareDeleted(groupName) {
      if (!this.ownsProject && (this.user.defaultGroup === groupName || this.user.groups.includes(groupName)))
        this.$router.push({name: 'home'})
    },
    addMode(addMode) {
      this.nbOpenShares += addMode ? 1 : -1
    },

    moment: function (value) {
      moment.locale(this.$i18n.locale);
      return moment(value);
    },
    maybeSaveProjectLabel() {
      if (this.newProjectLabel) {
        this.saveProjectLabel(this.newProjectLabel)
            .then(this.stopEditProjectLabel)
            .catch(this.stopEditProjectLabel);
      }
    },
    maybeSaveProjectDescription() {
      if (this.newProjectDescription || this.newProjectDescription.trim() === '') {
        this.saveProjectDescription(this.newProjectDescription)
            .then(this.stopEditProjectDescription)
            .catch(this.stopEditProjectDescription);
      }
    },
    maybeSaveProjectDate() {
      if (this.newProjectDate && this.newProjectDate.trim() !== '') {
        this.saveProjectDate(this.newProjectDate)
            .then(this.stopEditProjectDate)
            .catch(this.stopEditProjectDate);
      }
    },
    stopEditProjectLabel() {
      this.editProjectLabel = false;
      this.newProjectLabel = this.project.label;
    },
    stopEditProjectDescription() {
      this.editProjectDescription = false;
      this.newProjectDescription = this.project.description
    },
    stopEditProjectDate() {
      this.editProjectDate = false;
      this.newProjectDate = this.project.createdDate;
    },
    async getAdditionalProjectInfo() {
      const response = await api.get(`/projects/${this.project.name}/_info?computeMetrics=false&computeOwners=true`);
      const p = response.data
      this.privateProject = p.private !== undefined && p.private
      this.owner = p.owner
      this.groupName = p.groupName
      this.projectDescription = p.description
    },
  },
};
</script>

<style>
.removeBorder > .v-input__control > .v-input__slot::after {
  border-style: none !important;
}

.removeBorder > .v-input__control > .v-input__slot::before {
  border-style: none !important;
}

/* .removeBorder > .v-input__control > .v-text-field__details {
  display: none;
} */
</style>



